/**
 * F 229 — the entity-detail footer, restyled.
 *
 * F 228 built the History panel and the Used in section. This is the
 * presentation pass: F 211's type scale, spacing scale and palette,
 * applied to two surfaces F 211 never touched.
 *
 * NOTHING HERE CHANGES THE DATA. `historyOf` is called exactly as F 227
 * wrote it; `usedIn`'s store limitation is reported exactly as F 228
 * reported it. What changed is the words, the spacing and the weight.
 *
 * THE THREE FIXES THIS PASS MAKES, from the operator's screenshot:
 *   - raw ISO timestamps -> "Restored · 2 hours ago", exact on hover
 *   - camelCase field keys -> "Title", "Deleted at", and a human verb
 *   - a grey pill badge -> a plain count beside the section title
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Box, Stack, Text, Group, Collapse, UnstyledButton } from '@mantine/core';
import {
  IconHistory, IconLink, IconChevronRight,
  IconPlus, IconTrash, IconRestore, IconPencil, IconArrowRight,
} from '@tabler/icons-react';
import { historyOf, type HistoryRecord } from '../../../core/workspace/entityHistory';
import { EncryptionPanel } from './EncryptionPanel';
import {
  summariseRecord, relativeTime, exactTime, formatValue, inlineFields, fieldLabel,
} from './historyHumanize';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import './EntityDetailFooter.css';

export interface EntityDetailFooterProps {
  entityId: string | null;
}

const ICONS = { plus: IconPlus, trash: IconTrash, restore: IconRestore, pencil: IconPencil, move: IconArrowRight };

// ─────────────────────────────────────────────────────────────────────────────
// History
// ─────────────────────────────────────────────────────────────────────────────

const HistoryPanel: React.FC<{ entityId: string | null }> = ({ entityId }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const records = useMemo(() => (entityId ? historyOf(entityId) : []), [entityId]);

  const toggleRecord = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <Box data-testid="fc-history-panel">
      <UnstyledButton
        onClick={() => setOpen((o) => !o)}
        data-testid="fc-history-toggle"
        aria-expanded={open}
        className="fc-detail__disclosure"
      >
        <IconHistory size={15} aria-hidden="true" className="fc-detail__disclosure-icon" />
        <Text size="sm" fw={500} className="fc-detail__section-title">History</Text>
        {/* F 229 — the grey pill read as unfinished. A plain count beside
            the title carries the same information with less furniture. */}
        {records.length > 0 && (
          <Text size="xs" c="dimmed" data-testid="fc-history-count" className="fc-detail__count">
            {records.length}
          </Text>
        )}
        <IconChevronRight size={14} aria-hidden="true" className="fc-detail__chevron" data-open={open} />
      </UnstyledButton>

      <Collapse in={open}>
        <Stack gap={0} data-testid="fc-history-list" pt={2}>
          {records.length === 0 && (
            <Text size="xs" c="dimmed" data-testid="fc-history-empty" className="fc-detail__empty">
              No history yet. Edits will appear here.
            </Text>
          )}
          {records.map((r) => {
            const s = summariseRecord(r);
            const Icon = ICONS[s.icon];
            const isOpen = !!expanded[r.id];
            return (
              <Box key={r.id} data-testid={`fc-history-row-${r.id}`} className="fc-history__row">
                <UnstyledButton
                  onClick={() => toggleRecord(r.id)}
                  data-testid={`fc-history-row-toggle-${r.id}`}
                  aria-expanded={isOpen}
                  className="fc-history__rowbtn"
                >
                  <Group gap={8} wrap="nowrap" align="center">
                    <Icon size={14} aria-hidden="true" className="fc-history__icon" data-verb={s.verb} />
                    <Text size="xs" fw={500} data-testid={`fc-history-summary-${r.id}`} className="fc-history__verb">
                      {s.text}
                    </Text>
                    <Text size="xs" c="dimmed" className="fc-history__fields" data-testid={`fc-history-fields-${r.id}`}>
                      {inlineFields(r)}
                    </Text>
                    {r.actor && <Text size="xs" c="dimmed" className="fc-history__actor">{r.actor}</Text>}
                    <span className="fc-history__spacer" />
                    <Text
                      size="xs"
                      c="dimmed"
                      className="fc-history__time"
                      data-testid={`fc-history-time-${r.id}`}
                      title={exactTime(r.timestamp)}
                    >
                      {relativeTime(r.timestamp)}
                    </Text>
                  </Group>
                </UnstyledButton>

                {isOpen && (
                  <Stack gap={3} data-testid={`fc-history-detail-${r.id}`} className="fc-history__detail">
                    {Object.entries(r.changedFields ?? {}).map(([field, delta]) => (
                      <Group key={field} gap={8} wrap="nowrap" align="baseline"
                             data-testid={`fc-history-field-${r.id}-${field}`} className="fc-history__field">
                        <Text size="xs" fw={500} className="fc-history__fieldlabel">{fieldLabel(field)}</Text>
                        <Text size="xs" c="dimmed" className="fc-history__before">{formatValue(delta.before)}</Text>
                        <Text size="xs" c="dimmed" aria-hidden="true">→</Text>
                        <Text size="xs" data-testid={`fc-history-after-${r.id}-${field}`} className="fc-history__after">
                          {formatValue(delta.after)}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Used in
// ─────────────────────────────────────────────────────────────────────────────

/** More than this and rows get a kind sub-heading. */
export const GROUP_THRESHOLD = 5;

const UsedInPanel: React.FC<{ entityId: string | null; referrers?: Referrer[] }> = ({ entityId, referrers }) => {
  const [open, setOpen] = useState(false);
  const { selectPage } = useWorkspace();
  const rows = referrers ?? [];
  const grouped = rows.length > GROUP_THRESHOLD;

  // Group by kind only past the threshold; below it the kind column on
  // each row already says the same thing, and a heading per row is noise.
  const byKind = useMemo(() => {
    const m = new Map<string, Referrer[]>();
    for (const r of rows) {
      const k = r.kind || 'other';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    return [...m.entries()];
  }, [rows]);

  return (
    <Box data-testid="fc-usedin-panel">
      <UnstyledButton
        onClick={() => setOpen((o) => !o)}
        data-testid="fc-usedin-toggle"
        aria-expanded={open}
        className="fc-detail__disclosure"
      >
        <IconLink size={15} aria-hidden="true" className="fc-detail__disclosure-icon" />
        <Text size="sm" fw={500} className="fc-detail__section-title">Used in</Text>
        {rows.length > 0 && (
          <Text size="xs" c="dimmed" data-testid="fc-usedin-count" className="fc-detail__count">
            {rows.length}
          </Text>
        )}
        <IconChevronRight size={14} aria-hidden="true" className="fc-detail__chevron" data-open={open} />
      </UnstyledButton>

      <Collapse in={open}>
        <Stack gap={0} data-testid="fc-usedin-list" pt={2}>
          {!entityId && (
            <Text size="xs" c="dimmed" data-testid="fc-usedin-empty" className="fc-detail__empty">
              Nothing references this yet.
            </Text>
          )}
          {entityId && rows.length === 0 && (
            /* F 229 — F 228 wrote this as two clauses of debug prose. The
               distinction is real and stays; it is now one sentence. */
            <Text size="xs" c="dimmed" data-testid="fc-usedin-noindex" className="fc-detail__empty">
              No index on this store yet.
            </Text>
          )}
          {(grouped
            ? byKind.map(([kind, items]) => (
                <Box key={kind} data-testid={`fc-usedin-group-${kind}`}>
                  <Text size="xs" c="dimmed" className="fc-usedin__grouphead">{kind}</Text>
                  {items.map((r) => (
                    <UnstyledButton key={r.source} data-testid={`fc-usedin-row-${r.source}`}
                                    onClick={() => selectPage(r.source)} className="fc-usedin__row">
                      <Text size="xs" className="fc-usedin__label">{r.label ?? r.source}</Text>
                      {!grouped && <Text size="xs" c="dimmed" className="fc-usedin__kind">{r.kind}</Text>}
                    </UnstyledButton>
                  ))}
                </Box>
              ))
            : rows.map((r) => (
                <UnstyledButton key={r.source} data-testid={`fc-usedin-row-${r.source}`}
                                onClick={() => selectPage(r.source)} className="fc-usedin__row">
                  <Text size="xs" className="fc-usedin__label">{r.label ?? r.source}</Text>
                  <Text size="xs" c="dimmed" className="fc-usedin__kind">{r.kind}</Text>
                </UnstyledButton>
              )))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export interface Referrer {
  source: string;
  kind: string;
  label?: string;
}

// ─────────────────────────────────────────────────────────────────────────────

export const EntityDetailFooter: React.FC<
  EntityDetailFooterProps & { referrers?: Referrer[]; encryptionPassphrase?: string }
> = ({ entityId, referrers, encryptionPassphrase }) => (
  <Box data-testid="fc-entity-detail-footer" className="fc-detail">
    {/* F 243 — the encryption surface. F 237 stopped at its Stage 0 gate
        because the seven F 233 helpers were unreachable from
        `freshcards/core`; F 238 Item 1 shipped the re-exports. Mounted here
        so the toggle and the anchor editor sit with History and Used-in,
        the panels F 228 measured as the entity detail surface. */}
    {encryptionPassphrase !== undefined && (
      <EncryptionPanel entityId={entityId} passphrase={encryptionPassphrase} />
    )}
    <HistoryPanel entityId={entityId} />
    <UsedInPanel entityId={entityId} referrers={referrers} />
  </Box>
);

export default EntityDetailFooter;
